import {
  createFileRoute,
  useNavigate,
  useSearch,
} from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useLayoutEffect, useRef, type JSX } from 'react';

import { useUiStore } from '@/features/settings/ui-store.js';
import {
  isAccessibleWorkspaceId,
  shouldAdoptWorkspace,
} from '@/features/workspace/workspace-access.js';
import { workspacesQueryOptions } from '@/lib/api/workspaces.js';
import { useChat } from '@/integration/hooks/useChat.js';
import { ChatContext } from '@/integration/runtime/chatContext.js';
import {
  fetchConversation,
  ResourceArchivedError,
  SendRejectedError,
} from '@/integration/services/integration-api.js';
import { notify } from '@/lib/notify.js';
import { ChatView } from '@/ui/chat/ChatView.js';

export interface ChatsSearch {
  conversationId?: string;
}

export const Route = createFileRoute('/chats')({
  component: ChatsPage,
  validateSearch: (raw: Record<string, unknown>): ChatsSearch => ({
    conversationId:
      typeof raw.conversationId === 'string' && raw.conversationId.length > 0
        ? raw.conversationId
        : undefined,
  }),
});

/**
 * The chat surface. The artifact side panel the agent's generated files open into is NOT here: it is a
 * full-height rail in `AppShell`, next to this column, so the Header stays with the chat exactly as it
 * does beside the left sidebar.
 */
function ChatsPage(): JSX.Element {
  const chat = useChat();
  const { conversationId } = useSearch({ from: '/chats' });
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Subscribe to the pending draft so a launcher navigation (e.g. from /home) that does not remount
  // this route still picks up the new draft and fires the layout effect below.
  const pendingDraft = useUiStore((s) => s.pendingDraft);
  const hasPending = pendingDraft != null;

  // Switching the workspace must leave the open chat: a conversation belongs to exactly one
  // workspace. WorkspaceSwitcher navigates to `/home` on click; this effect is the backup — it aborts
  // streams via `newChat()` and navigates to `/home` when the store changes for a user switch. Guard
  // on a ref of the previous id so first mount is a no-op — only a genuine id change while mounted
  // fires (legacy `/projects/:id` → `/chats` is not treated as a sidebar switch).
  const selectedWorkspaceId = useUiStore((s) => s.selectedWorkspaceId);
  const hasHydrated = useUiStore((s) => s.hasHydrated);
  const prevWorkspaceIdRef = useRef<string | null | undefined>(undefined);
  const ignoreStaleUrlConversationRef = useRef(false);
  useLayoutEffect(() => {
    if (!hasHydrated) return;
    if (prevWorkspaceIdRef.current === undefined) {
      prevWorkspaceIdRef.current = selectedWorkspaceId;
      return;
    }
    if (prevWorkspaceIdRef.current === selectedWorkspaceId) return;
    prevWorkspaceIdRef.current = selectedWorkspaceId;
    if (useUiStore.getState().lastWorkspaceChangeReason !== 'user') return;
    ignoreStaleUrlConversationRef.current = true;
    chat.newChat();
    void navigate({ to: '/home', replace: true });
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [selectedWorkspaceId, hasHydrated]);

  useEffect(() => {
    // Don't project a stale activeConversationId back into the URL when the
    // user is on their way to start a NEW chat (pending draft present, or URL
    // has no conversationId because of an intentional navigate-to-blank-chats).
    // Without this guard, a launcher's `navigate({ to: '/chats' })` would be
    // reverted to the previously active conv and the next send would land in
    // the wrong chat.
    if (
      !chat.activeConversationId ||
      chat.activeConversationId === conversationId
    )
      return;
    if (hasPending) {
      // eslint-disable-next-line no-console
      console.warn(
        '[project-flow] skip stale activeConv→URL redirect — pending draft present',
      );
      return;
    }
    void navigate({
      to: '/chats',
      search: { conversationId: chat.activeConversationId } as never,
      replace: true,
    });
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [chat.activeConversationId, hasPending]);

  // When the active chat is deleted, redirect to home.
  useEffect(() => {
    if (!chat.deletedActiveId) return;
    chat.clearDeletedActiveId();
    void navigate({ to: '/', replace: true });
  }, [chat.deletedActiveId, chat.clearDeletedActiveId, navigate]);

  useEffect(() => {
    if (!conversationId) return;
    if (ignoreStaleUrlConversationRef.current) {
      ignoreStaleUrlConversationRef.current = false;
      return;
    }
    if (chat.activeConversationId === conversationId) return;
    if (chat.streamingConvId === conversationId) return;
    void chat.selectConversation(conversationId);
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [conversationId]);

  // A deep-link can open a chat from a DIFFERENT workspace than the selected one (or a legacy chat
  // with no workspace). The main pane loads it by id above, but the workspace-scoped sidebar wouldn't
  // list it — the two panes disagree. Resolve the opened chat's own workspace and adopt it as the
  // selection so both agree. Sync `prevWorkspaceIdRef` to the adopted id BEFORE the store write so the
  // workspace-switch effect above treats this programmatic switch as a no-op and does NOT drop the
  // chat we're opening. A null conversation workspace (legacy chat) leaves the selection untouched.
  useEffect(() => {
    if (!conversationId) return;
    let cancelled = false;
    const workspaceAtStart = useUiStore.getState().selectedWorkspaceId;
    void (async () => {
      try {
        const conv = await fetchConversation(conversationId);
        if (cancelled) return;
        const state = useUiStore.getState();
        if (state.selectedWorkspaceId !== workspaceAtStart) return;
        const conversationWorkspaceId = conv.workspaceId ?? null;
        if (conversationWorkspaceId) {
          const workspaces = await queryClient.ensureQueryData(
            workspacesQueryOptions(),
          );
          if (cancelled) return;
          if (!isAccessibleWorkspaceId(conversationWorkspaceId, workspaces)) {
            void navigate({ to: '/chats', search: {}, replace: true });
            return;
          }
        }
        if (
          !shouldAdoptWorkspace(
            conversationWorkspaceId,
            state.selectedWorkspaceId,
          )
        )
          return;
        prevWorkspaceIdRef.current = conversationWorkspaceId;
        state.setSelectedWorkspaceId(conversationWorkspaceId);
      } catch {
        // Best-effort: a failed lookup just leaves the current workspace selection in place.
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [conversationId]);

  // Store-based: pending draft + attachments from the Home launcher composer.
  // useLayoutEffect fires BEFORE child useEffect (Composer's pendingFiles pickup),
  // ensuring we send the message rather than the Composer stealing the files.
  // The pendingDraft dep so a launcher navigation that does NOT remount the route
  // (TanStack keeps the same component instance) still triggers the new send.
  useLayoutEffect(() => {
    const state = useUiStore.getState();
    const text = state.pendingDraft;
    const atts = state.pendingAttachments;
    /** Skill chips picked in the launcher composer before the first send — see
     *  `pendingSkillIds`'s doc comment for why this carrier exists at all. */
    const skillIds = state.pendingSkillIds;
    // A launcher composer may have reserved a conversation id so its attachment uploads are keyed to
    // it. Send into that SAME conversation, not a fresh one.
    const reservedConvId = state.pendingConversationId;

    if (!text && atts.length === 0 && skillIds.length === 0) return;

    state.setPendingDraft(null);
    state.setPendingAttachments([]);
    state.setPendingConversationId(null);
    state.setPendingSkillIds([]);

    // newChat() aborts any in-flight stream and clears messages + active/streaming ids before the new
    // sendMessage. Without this, a launcher navigation while a previous chat was open would send the
    // new message INTO the previous conversation.
    chat.newChat();

    void chat
      .sendMessage(text ?? '', {
        attachments: atts.length > 0 ? atts : undefined,
        // Target the reserved conversation (where the uploads live) instead of creating a new one.
        conversationId: reservedConvId ?? undefined,
        skillIds: skillIds.length > 0 ? skillIds : undefined,
      })
      .catch(async (error: unknown) => {
        // The Home composer has already unmounted. Put the non-persisted text into the live chat
        // composer instead of losing it behind this fire-and-forget launcher handoff.
        if (reservedConvId) {
          await chat.selectConversation(reservedConvId).catch(() => undefined);
        }
        const currentState = useUiStore.getState();
        currentState.setComposerPrefillAttachments(atts);
        currentState.setComposerPrefillSkillIds(skillIds);
        currentState.setComposerPrefillRestoresDraft(true);
        currentState.setComposerPrefill(text ?? '');
        const alreadyNotified =
          (error instanceof SendRejectedError ||
            error instanceof ResourceArchivedError) &&
          error.alreadyNotified;
        if (!alreadyNotified) {
          notify.warning(
            error instanceof Error
              ? error.message
              : 'Could not send this message.',
          );
        }
      });
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [pendingDraft]);

  return (
    <ChatContext.Provider value={{ ...chat, supportsDeveloperReplies: true }}>
      <ChatView />
    </ChatContext.Provider>
  );
}
